import {inject} from '@angular/core';
import type {CanDeactivateFn} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {ConfirmationService} from 'primeng/api';

/** Implemented by any page hosting an editable client form. */
export interface HasUnsavedChanges {
  hasUnsavedChanges(): boolean;
}

/**
 * Confirms before abandoning a partially filled client form.
 *
 * Uses the app-wide `ConfirmationService` / `<p-confirmDialog>` already
 * mounted in `app.html`, matching how the list pages confirm destructive
 * actions. Browser-level navigation (refresh, close, external link) is covered
 * separately by the `beforeunload` listener inside the registration form.
 */
export const clientFormUnsavedGuard: CanDeactivateFn<HasUnsavedChanges> = (
  component,
) => {
  if (!component?.hasUnsavedChanges?.()) return true;

  const confirmation = inject(ConfirmationService);
  const translate = inject(TranslateService);

  return new Promise<boolean>((resolve) => {
    confirmation.confirm({
      header: translate.instant('clients.form.leaveTitle'),
      message: translate.instant('clients.form.leaveMessage'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: translate.instant('clients.form.leaveConfirm'),
      rejectLabel: translate.instant('clients.form.leaveStay'),
      acceptButtonStyleClass: 'p-button-danger',
      rejectButtonStyleClass: 'p-button-cancel',
      accept: () => resolve(true),
      reject: () => resolve(false),
    });
  });
};
